import { useMemo, useState } from "react";
import { FileUp, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ONBOARDING_MAX_EMPLOYEE_ROWS,
  mergeDuplicateEmails,
  parseEmployeeCsv,
  suggestTeams,
  validateEmployeeRow,
  type EmployeeImportColumn,
  type EmployeeImportRow,
  type EmployeeRowErrors,
} from "@graftus/schema";
import type { SectorWords } from "@/hooks/use-sector-words";
import { buildCsvTemplate, buildSampleCsv, csvDataHref } from "@/lib/onboarding/csv-template";
import { StepHeading } from "../OnboardingShell";
import { StepFooter } from "../StepFooter";
import { LinkButton } from "../ui/buttons";
import { Dropzone } from "@/components/ui/dropzone";
import { EditableTable, type EditableColumn } from "../ui/EditableTable";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { SummaryBanner } from "../ui/SummaryBanner";
import { ManualAddForm } from "./ManualAddForm";

export const MAX_CSV_BYTES = 2 * 1024 * 1024;

export interface ImportSummary {
  created: number;
  updated: number;
  invalid: number;
  teamsCreated: number;
}

interface EmployeesStepProps {
  words: SectorWords;
  rows: EmployeeImportRow[];
  fileName: string | null;
  onRowsChange: (rows: EmployeeImportRow[], fileName: string | null) => void;
  /** Rows already imported in a previous visit (the table is empty then). */
  importSummary: ImportSummary | null;
  /** Server-side errors from a failed import, by row id. */
  importErrors: Record<string, EmployeeRowErrors>;
  onNext: () => void;
  onBack?: () => void;
  saving?: boolean;
  error?: string | null;
}

export function rowErrorsFor(
  rows: EmployeeImportRow[],
  importErrors: Record<string, EmployeeRowErrors>,
): Record<string, EmployeeRowErrors> {
  const out: Record<string, EmployeeRowErrors> = {};
  for (const row of rows) {
    const errors = { ...importErrors[row.id], ...validateEmployeeRow(row) };
    if (Object.keys(errors).length > 0) out[row.id] = errors;
  }
  return out;
}

let rowCounter = 0;
function newRowId(): string {
  rowCounter += 1;
  return `r${Date.now().toString(36)}${rowCounter}`;
}

/** Step 4: a CSV or people one by one, validated and fixable in the table. */
export function EmployeesStep({
  words,
  rows,
  fileName,
  onRowsChange,
  importSummary,
  importErrors,
  onNext,
  onBack,
  saving = false,
  error,
}: EmployeesStepProps) {
  const { t } = useTranslation("onboarding");
  const [mode, setMode] = useState<"csv" | "manual">("csv");
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mergedCount, setMergedCount] = useState(0);

  const labels = {
    name: t("employees.columns.name"),
    email: t("employees.columns.email"),
    mobile: t("employees.columns.mobile"),
    role: t("employees.columns.role"),
    employeeNumber: t("employees.columns.employeeNumber"),
  };
  const columns: EditableColumn[] = [
    { key: "name", label: labels.name, placeholder: "Förnamn Efternamn" },
    { key: "email", label: labels.email, placeholder: `namn@${words.domain}`, inputMode: "email" },
    { key: "mobile", label: labels.mobile, placeholder: "070-…", inputMode: "tel" },
    { key: "team", label: words.teamCap, placeholder: words.deptEx },
    { key: "role", label: labels.role, placeholder: words.roleEx },
    { key: "employeeNumber", label: labels.employeeNumber, placeholder: "1001", inputMode: "numeric" },
  ];

  const errors = useMemo(() => rowErrorsFor(rows, importErrors), [rows, importErrors]);
  const bad = Object.keys(errors).length;
  const teams = useMemo(() => suggestTeams(rows).map((s) => s.key), [rows]);

  const loadCsv = (text: string, name: string) => {
    setFileError(null);
    const parsed = parseEmployeeCsv(text, { makeId: () => newRowId() });
    if (parsed.missingRequiredColumns.length > 0) {
      const missing = parsed.missingRequiredColumns
        .map((col) => (col === "team" ? words.teamCap : labels[col]))
        .join(", ");
      setFileError(t("employees.dropzone.errors.missingColumns", { columns: missing }));
      return;
    }
    if (parsed.rows.length === 0) {
      setFileError(t("employees.dropzone.errors.empty"));
      return;
    }
    const merged = mergeDuplicateEmails([...rows, ...parsed.rows]);
    if (merged.rows.length > ONBOARDING_MAX_EMPLOYEE_ROWS) {
      setFileError(t("employees.dropzone.errors.tooMany", { max: ONBOARDING_MAX_EMPLOYEE_ROWS }));
      return;
    }
    setMergedCount(merged.merged);
    onRowsChange(merged.rows, name);
  };

  const onFile = (file: File) => {
    setFileError(null);
    if (!/\.csv$/i.test(file.name) && !file.type.includes("csv") && !file.type.includes("text")) {
      setFileError(t("employees.dropzone.errors.notCsv"));
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setFileError(t("employees.dropzone.errors.tooLarge"));
      return;
    }
    setReading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setReading(false);
      loadCsv(String(reader.result ?? ""), file.name);
    };
    reader.onerror = () => {
      setReading(false);
      setFileError(t("employees.dropzone.errors.empty"));
    };
    reader.readAsText(file, "utf-8");
  };

  const changeCell = (rowId: string, column: EmployeeImportColumn, value: string) => {
    onRowsChange(
      rows.map((row) => (row.id === rowId ? { ...row, [column]: value } : row)),
      fileName,
    );
  };
  const removeRow = (rowId: string) => {
    onRowsChange(rows.filter((row) => row.id !== rowId), fileName);
  };
  const addRow = (row: Omit<EmployeeImportRow, "id">) => {
    const merged = mergeDuplicateEmails([...rows, { ...row, id: newRowId() }]);
    onRowsChange(merged.rows, fileName);
  };

  const importedTotal = importSummary ? importSummary.created + importSummary.updated : 0;
  const hint =
    rows.length === 0 && importedTotal === 0
      ? t("employees.hint.empty")
      : bad > 0
        ? t("employees.hint.fix", { count: bad })
        : null;

  return (
    <div data-testid="onboarding-employees">
      <StepHeading title={t("employees.title")} lead={t("employees.lead")} />

      {importSummary && rows.length === 0 ? (
        <div className="mb-5">
          <SummaryBanner
            tone="success"
            title={t("employees.summary.imported", { n: importedTotal })}
            body={t("employees.summary.addMore")}
          />
        </div>
      ) : null}

      <div className="mb-5">
        <SegmentedControl
          aria-label={t("employees.title")}
          value={mode}
          onChange={setMode}
          options={[
            { value: "csv", label: t("employees.modes.csv"), icon: <FileUp className="h-4 w-4" aria-hidden="true" /> },
            { value: "manual", label: t("employees.modes.manual"), icon: <PenLine className="h-4 w-4" aria-hidden="true" /> },
          ]}
        />
      </div>

      {mode === "csv" && rows.length === 0 ? (
        <Dropzone
          onFile={onFile}
          title={t("employees.dropzone.title")}
          body={t("employees.dropzone.body")}
          columns={[labels.name, labels.email, labels.mobile, words.teamCap, labels.role, labels.employeeNumber]}
          reading={reading}
          error={fileError}
          footer={
            <>
              <a
                href={csvDataHref(buildCsvTemplate(labels, words))}
                download="medarbetare-mall.csv"
                className="font-semibold text-g-primary underline-offset-4 hover:underline"
              >
                {t("employees.dropzone.template")}
              </a>
              {import.meta.env.DEV ? (
                <LinkButton onClick={() => loadCsv(buildSampleCsv(labels, words), "exempel.csv")}>
                  {t("employees.dropzone.sample")}
                </LinkButton>
              ) : null}
            </>
          }
        />
      ) : null}

      {mode === "manual" ? <ManualAddForm words={words} teams={teams} onAdd={addRow} /> : null}

      {rows.length > 0 ? (
        <div className="mt-5 flex flex-col gap-3">
          <SummaryBanner
            tone={bad > 0 ? "warning" : "success"}
            title={
              bad > 0
                ? t("employees.summary.fix", { n: rows.length, bad })
                : t("employees.summary.ready", { n: rows.length })
            }
            body={[
              fileName,
              bad > 0 ? t("employees.summary.fixBelow") : t("employees.summary.allGood"),
              mergedCount > 0 ? t("employees.summary.merged", { n: mergedCount }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            action={
              <LinkButton onClick={() => { onRowsChange([], null); setMergedCount(0); setMode("csv"); }}>
                {t("employees.summary.replaceFile")}
              </LinkButton>
            }
          />
          <EditableTable
            columns={columns}
            rows={rows}
            errors={errors}
            errorLabel={(code) => t(`employees.errors.${code}`)}
            onChange={changeCell}
            onRemove={removeRow}
            removeLabel={t("employees.remove")}
            datalists={{ team: "onboarding-table-teams" }}
          />
          <datalist id="onboarding-table-teams">
            {teams.map((team) => (
              <option key={team} value={team} />
            ))}
          </datalist>
        </div>
      ) : null}

      {error ? (
        <p className="mt-4 rounded-g-md border border-g-danger bg-g-danger-tint px-4 py-3 text-[14px] font-medium text-g-danger" role="alert">
          {error}
        </p>
      ) : null}

      <StepFooter
        primaryLabel={t("employees.next")}
        onPrimary={onNext}
        primaryDisabled={(rows.length === 0 && importedTotal === 0) || bad > 0}
        loading={saving}
        hint={hint}
        onBack={onBack}
      />
    </div>
  );
}
